import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Skeleton } from '../../atoms/Skeleton'
import { ProfileForm } from './ProfileForm'
import { updateMyProfile } from '../../../lib/api'
import { COMPLETION_FIELDS, MANAGER_ONLY_HINT, toDraft, type FormField } from '../../../lib/profile'
import { myProfileQuery, staffKeys } from '../../../queries/staff'
import { sessionKeys } from '../../../queries/session'
import type { MyProfile, ProfilePatch } from '../../../types'

interface MyProfileFormProps {
	fields?: readonly FormField[]
	submitLabel: string
}

export function MyProfileForm({ fields, submitLabel }: MyProfileFormProps) {
	const queryClient = useQueryClient()
	const { data: profile, dataUpdatedAt } = useQuery(myProfileQuery())

	if (!profile) {
		return (
			<div className="flex flex-col gap-3">
				{Array.from({ length: 3 }).map((_, index) => (
					<Skeleton key={index} className="h-40 w-full rounded-lg" />
				))}
			</div>
		)
	}

	const allFields: FormField[] = profile.start_date ? [...COMPLETION_FIELDS, 'start_date'] : [...COMPLETION_FIELDS]
	const shown = fields ?? allFields
	const editable = shown.filter((field) => field === 'name' || (field !== 'start_date' && !profile.locked_fields.includes(field)))

	async function save(patch: ProfilePatch) {
		const saved = await updateMyProfile(patch)
		queryClient.setQueryData<MyProfile>(staffKeys.myProfile(), saved)
		if (patch.name !== undefined) await queryClient.invalidateQueries()
		else
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: sessionKeys.me() }),
				queryClient.invalidateQueries({ queryKey: staffKeys.all }),
			])
		toast.success('Profile saved')
	}

	return (
		<ProfileForm
			key={dataUpdatedAt}
			initial={toDraft(profile, profile.missing_fields.includes('name') ? '' : profile.name)}
			shown={shown}
			editable={editable}
			required={['name']}
			lockedHint={MANAGER_ONLY_HINT}
			submitLabel={submitLabel}
			onSubmit={save}
		/>
	)
}
